import { useEffect } from 'react'
import { useUserSettingStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'

/**
 * Per-user setting key: hide the vertical dividers between table body columns.
 * Stored as JSON (`"true"` / `"false"`) so it reads the same through `useSettings`.
 */
export const HIDE_TABLE_COLUMN_LINES_SETTING = `hideTableColumnLines`

/** Class the CSS rule hangs off, applied to <html> so every table follows. */
const HIDE_COLUMN_LINES_CLASS = `table-columns-borderless`

/**
 * Loads the column-lines preference once and mirrors it onto <html>.
 *
 * Call it from the app shell, not from a table. Driving the change through a
 * class on the root means the setting reaches tables that have no toolbar of
 * their own, and that flipping it never re-renders a virtualized grid.
 *
 * The caller must be an `observer()`: the value lives on an observable store.
 */
export function useTableColumnLinesSync(): void {
  const store = useUserSettingStore()
  const hidden = store.settings[HIDE_TABLE_COLUMN_LINES_SETTING] === `true`

  useEffect(() => {
    bg(async () => store.get(HIDE_TABLE_COLUMN_LINES_SETTING))
  }, [store])

  useEffect(() => {
    document.documentElement.classList.toggle(HIDE_COLUMN_LINES_CLASS, hidden)
    // The class outlives this hook otherwise. Nothing but tables reads it, but a
    // shell unmount (an auth transition, say) would leave it stamped on a document
    // that no longer has an owner for it.
    return () => document.documentElement.classList.remove(HIDE_COLUMN_LINES_CLASS)
  }, [hidden])
}
